import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Container, Row, Col, Button, Table } from 'react-bootstrap';
import MapBrowser from '../track/map';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip, ReferenceDot } from 'recharts';

function Device(id, spd, vmg, ang, pos, rotate, lines, main, wind, wds, wdd, all_lines, name, unit, wind_unit,
    high10sSpdSegments, // g.spd >= max_10s_spd
    high10s95perSpdSegments, // g.spd >= max_10s_spd*0.95
    aveSpdSegments,     // g.spd >= ave_speed
    maxSpdSegments      // g.spd >= max_speed
) {
    this.id = id;
    this.spd = spd;
    this.vmg = vmg;
    this.ang = ang;
    this.pos = pos;
    this.rotate = rotate;
    this.all_lines = all_lines;
    this.lines = lines;
    this.coloredLines = lines;
    this.main = main;
    this.wind = wind;
    this.windspd = wds;
    this.winddir = wdd;
    this.isStarboard = true;
    this.name = name;
    this.unit = unit;
    this.wind_unit = wind_unit;
    this.high10sSpdSegments = high10sSpdSegments;
    this.high10s95perSpdSegments = high10s95perSpdSegments;
    this.aveSpdSegments = aveSpdSegments;
    this.maxSpdSegments = maxSpdSegments;
}

class Tack extends Component {
    constructor(props) {
        super(props);
        this.state = {
            tacks: [],
            minSpeedThreshold: (this.props.authUser && this.props.authUser.tack_min_speed_threshold) ? this.props.authUser.tack_min_speed_threshold : 20, // km/h デフォルト
            entrySpeedThreshold: (this.props.authUser && this.props.authUser.tack_entry_speed_filter) ? this.props.authUser.tack_entry_speed_filter : 20, // km/h デフォルト
            selectedTackIndex: null,
            deviceMap: {},
            center: { lat: 35.301318, lng: 139.480219 },
            graphData: [],
            zoom: 18,
            yaxis: false,
            // keys for graph
            dataKeyStb: "spd_s",
            dataKeyPt: "spd_p",
            currentSegmentLines: [],
            markers: [], // for map
            chartReferenceDots: [], // for chart points
            heightLine: null, // for map height loss line
            // Sorting & Filtering
            sortConfig: { key: null, direction: 'ascending' },
            filterTurn: 'All',
            filterResult: 'All'
        };
        this.handleAnalyze = this.handleAnalyze.bind(this);
        this.handleRowClick = this.handleRowClick.bind(this);
        this.handleMouseMove = this.handleMouseMove.bind(this);
        this.handleChartClick = this.handleChartClick.bind(this);
    }

    componentDidMount() {
        // Suppress ResizeObserver loop limit exceeded error
        const originalError = console.error;
        console.error = (...args) => {
            if (typeof args[0] === 'string' && /ResizeObserver loop/.test(args[0])) {
                return;
            }
            originalError.apply(console, args);
        };

        if (this.props.target) {
            this.handleAnalyze();
        }
    }

    componentDidUpdate(prevProps) {
        if (this.props.target !== prevProps.target) {
            this.handleAnalyze();
        }
    }

    normalizeDiff(dir, wind) {
        let d = dir - wind;
        if (d > 180) d -= 360;
        if (d < -180) d += 360;
        return d;
    }

    // Helper to calc height metric (proj dist against wind)
    calcHeightMetric(lat, lon, windDir, baseLat, baseLon) {
        const rad = Math.PI / 180;
        const R = 6371000; // earth radius meters
        const x = (lon - baseLon) * rad * R * Math.cos(baseLat * rad);
        const y = (lat - baseLat) * rad * R;

        const windRad = windDir * rad;
        // Upwind vector components (N=0, E=90) -> (sin, cos)
        const ux = Math.sin(windRad);
        const uy = Math.cos(windRad);

        return x * ux + y * uy;
    }

    handleAnalyze() {
        if (!this.props.target || !this.props.target.gpses) return;

        const gpses = this.props.target.gpses;
        const windDir = this.props.target.wind_direction;
        const HOLD_SECONDS = 10;
        const PADDING_SECONDS = 20; // ターン前後の余白秒数
        const minSpeedThreshold = this.state.minSpeedThreshold;
        const entrySpeedThreshold = this.state.entrySpeedThreshold;

        let tacks = [];
        let inTurn = false;
        let turnStartIndex = 0;
        let holdUntilTime = null;
        let prevDiff = null;
        let turnDirection = "";

        for (let i = 0; i < gpses.length; i++) {
            const gps = gpses[i];
            if (!gps.time) continue;

            const curDiff = this.normalizeDiff(gps.direction, windDir);

            if (prevDiff === null) {
                prevDiff = curDiff;
                continue;
            }

            // wind_direction を跨いだか
            // 0度ジャストを含む判定 (例: 0 -> 10)
            const signChanged =
                (prevDiff <= 0 && curDiff > 0) ||
                (prevDiff >= 0 && curDiff < 0);

            // Tack検出条件:
            // 1. 最短経路が0度をまたぐ (差 < 180)
            // 2. 風上セクター内での動作であること (角度 < 100度) -> Jibe(風下発)やRoundingを除外
            const isUpwindSector = Math.abs(prevDiff) < 100 && Math.abs(curDiff) < 100;
            const crossedWind = signChanged && (Math.abs(prevDiff - curDiff) < 180) && isUpwindSector;

            // --- ターン開始 ---
            if (!inTurn && crossedWind) {
                inTurn = true;
                turnStartIndex = i;
                holdUntilTime = gps.time.clone().add(HOLD_SECONDS, "seconds");

                turnDirection =
                    prevDiff < 0 && curDiff > 0
                        ? "Starboard → Port"
                        : "Port → Starboard";
            }

            // --- ターン中 ---
            if (inTurn) {
                // 再クロスしたらキャンセル
                if (crossedWind && i !== turnStartIndex) {
                    inTurn = false;
                    holdUntilTime = null;
                }

                // 10秒維持で確定
                if (holdUntilTime && gps.time.isAfter(holdUntilTime)) {
                    // --- セグメント範囲の決定 (前後20秒含む) ---
                    let startIdx = turnStartIndex;
                    let endIdx = i;

                    // startIdx を20秒前に拡張
                    const startTime = gpses[turnStartIndex].time;
                    const paddedStartTime = startTime.clone().subtract(PADDING_SECONDS, "seconds");
                    while (startIdx > 0 && gpses[startIdx - 1].time && gpses[startIdx - 1].time.isAfter(paddedStartTime)) {
                        startIdx--;
                    }

                    // endIdx を20秒後に拡張
                    const endTime = gpses[endIdx].time;
                    const paddedEndTime = endTime.clone().add(PADDING_SECONDS, "seconds");
                    while (endIdx < gpses.length - 1 && gpses[endIdx + 1].time && gpses[endIdx + 1].time.isBefore(paddedEndTime)) {
                        endIdx++;
                    }

                    // 表示用セグメント (拡張版)
                    const extendedSegment = gpses.slice(startIdx, endIdx + 1);

                    // 分析用コアセグメント (元のロジック通り、ターン開始〜10秒後まで)
                    const coreSegment = gpses.slice(turnStartIndex, i);

                    // --- 数値計算とポイント特定 ---
                    // Entry Speed Point
                    const entrySegment = gpses.slice(startIdx, turnStartIndex + 1);
                    let maxSpeedPt = null;
                    let maxSpeed = -1;
                    let maxSpeedIndex = -1; // Index in extendedSegment (since entrySegment starts at 0 of extendedSegment)

                    if (entrySegment.length > 0) {
                        entrySegment.forEach((g, idx) => {
                            const s = g._speed ?? g.speed ?? 0;
                            if (s >= maxSpeed) {
                                maxSpeed = s;
                                maxSpeedPt = g;
                                maxSpeedIndex = idx;
                            }
                        });
                    }

                    // Min Speed Point
                    let minSpeedPt = null;
                    let minSpeed = Infinity;

                    if (coreSegment.length > 0) {
                        coreSegment.forEach(g => {
                            const s = g._speed ?? g.speed ?? 0;
                            if (s < minSpeed) {
                                minSpeed = s;
                                minSpeedPt = g;
                            }
                        });
                    }

                    // Fallback
                    if (maxSpeed === -1) maxSpeed = 0;
                    if (minSpeed === Infinity) minSpeed = 0;

                    // Elapsed Time: Duration between Entry Speed Point (Max) and Min Speed Point
                    let elapsedTime = minSpeedPt && maxSpeedPt ? minSpeedPt.time.diff(maxSpeedPt.time) / 1000 : 0;

                    // --- Height Loss Calculation (New Specification) ---
                    // Start: Max Speed Point (maxSpeedPt)
                    // Direction: Upwind (windDir)
                    // End: First intersection with track

                    const baseLat = extendedSegment[0].lat;
                    const baseLon = extendedSegment[0].lon;

                    let pStart = maxSpeedPt;
                    let pCross = null;
                    let heightDiffVal = 0;
                    let bestT = Infinity;

                    if (pStart && maxSpeedIndex !== -1) {
                        // Calculate Upwind Ray
                        const rad = Math.PI / 180;
                        const upwindRad = windDir * rad; // Wind comes FROM windDir. Upwind is towards windDir.
                        const vx = Math.sin(upwindRad);
                        const vy = Math.cos(upwindRad);

                        // XY conversion consts
                        const R = 6371000;
                        const ky = rad * R;
                        const kx = rad * R * Math.cos(baseLat * rad);

                        const toXY = (p) => ({
                            x: (p.lon - baseLon) * kx,
                            y: (p.lat - baseLat) * ky
                        });

                        const A = toXY(pStart); // Ray Origin

                        // Find Intersection with Track Segments *after* pStart (maxSpeedPt)
                        for (let k = maxSpeedIndex + 1; k < extendedSegment.length - 1; k++) {
                            const C = toXY(extendedSegment[k]);
                            const D = toXY(extendedSegment[k + 1]);

                            // Solve: A + t*V = C + s*(D-C)
                            const dx_seg = D.x - C.x;
                            const dy_seg = D.y - C.y;

                            const det = vx * (-dy_seg) - vy * (-dx_seg);
                            if (Math.abs(det) < 1e-9) continue; // Parallel

                            const diffX = C.x - A.x;
                            const diffY = C.y - A.y;

                            const t = (diffX * (-dy_seg) - diffY * (-dx_seg)) / det;
                            const s = (vx * diffY - vy * diffX) / det;

                            if (t > 1.0 && s >= 0 && s <= 1) { // t > 1m
                                if (t < bestT) {
                                    bestT = t;
                                    // Calc intersection point
                                    const ix = A.x + t * vx;
                                    const iy = A.y + t * vy;

                                    // Convert back to LatLon
                                    pCross = {
                                        lat: (iy / ky) + baseLat,
                                        lon: (ix / kx) + baseLon
                                    };
                                }
                            }
                        }
                    }

                    let heightDiffStr = "-";
                    if (pCross) {
                        heightDiffVal = bestT;
                        heightDiffStr = heightDiffVal.toFixed(0);
                    }

                    const result =
                        minSpeed < minSpeedThreshold ? "Fail" : "Success";

                    let entryAngle = "-";
                    if (maxSpeedPt) {
                        entryAngle = (90 - Math.abs(this.normalizeDiff(maxSpeedPt.direction, windDir))).toFixed(0);
                    }

                    // --- 追加フィルタ: 最低速度地点(minSpeedPt)が最高速度地点(maxSpeedPt)より風下にあってはいけない ---
                    // Tackの場合、減速(minSpeed)地点はターン頂点付近＝風上に近いはずであり、
                    // 減速地点がエントリー(maxSpeed)より風下にある＝ベアして加速した後の失速などを拾っている可能性がある (Roundingなど)
                    let isValidTack = true;
                    if (maxSpeedPt && minSpeedPt) {
                        const hMax = this.calcHeightMetric(maxSpeedPt.lat, maxSpeedPt.lon, windDir, baseLat, baseLon);
                        const hMin = this.calcHeightMetric(minSpeedPt.lat, minSpeedPt.lon, windDir, baseLat, baseLon);

                        // hMin (Tack点付近) < hMax (Entry点) -> Tack点はEntryより風下にある -> NG
                        if (hMin < hMax) {
                            isValidTack = false;
                            // console.log("Skipping invalid tack: minSpeed downwind of maxSpeed");
                        }
                    }

                    if (maxSpeed >= entrySpeedThreshold && isValidTack) {
                        tacks.push({
                            originalIndex: tacks.length,
                            turnDirection,
                            entrySpeed: maxSpeed.toFixed(2),
                            entryAngle,
                            minSpeed: minSpeed.toFixed(2),
                            elapsedTime: elapsedTime.toFixed(1),
                            heightDiff: heightDiffStr,
                            result,
                            segment: extendedSegment,
                            maxSpeedPt,
                            minSpeedPt,
                            pHigh: maxSpeedPt,
                            pCross
                        });
                    }

                    inTurn = false;
                    holdUntilTime = null;
                }
            }

            prevDiff = curDiff;
        }

        // Auto-select first tack if available
        const firstTack = tacks.length > 0 ? tacks[0] : null;

        this.setState({
            tacks,
            selectedTackIndex: null,
            deviceMap: {},
            graphData: [],
            currentSegmentLines: [],
            markers: [],
            chartReferenceDots: [],
            heightLine: null
        }, () => {
            if (firstTack) {
                this.handleRowClick(firstTack);
            }
        });
    }

    requestSort(key) {
        let direction = 'ascending';
        if (this.state.sortConfig.key === key && this.state.sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        this.setState({ sortConfig: { key, direction } });
    }

    getProcessedTacks() {
        let tacks = [...this.state.tacks];

        // Filtering
        if (this.state.filterTurn !== 'All') {
            if (this.state.filterTurn === 'Starboard') {
                tacks = tacks.filter(j => j.turnDirection.startsWith('Starboard'));
            } else if (this.state.filterTurn === 'Port') {
                tacks = tacks.filter(j => j.turnDirection.startsWith('Port'));
            }
        }
        if (this.state.filterResult !== 'All') {
            tacks = tacks.filter(j => j.result === this.state.filterResult);
        }

        // Sorting
        if (this.state.sortConfig.key !== null) {
            tacks.sort((a, b) => {
                let aVal = a[this.state.sortConfig.key];
                let bVal = b[this.state.sortConfig.key];

                // Numeric convert if needed
                if (['entrySpeed', 'entryAngle', 'minSpeed', 'elapsedTime'].includes(this.state.sortConfig.key)) {
                    aVal = parseFloat(aVal);
                    bVal = parseFloat(bVal);
                }
                if (this.state.sortConfig.key === 'heightDiff') {
                    aVal = aVal === '-' ? -999 : parseFloat(aVal);
                    bVal = bVal === '-' ? -999 : parseFloat(bVal);
                }

                if (aVal < bVal) return this.state.sortConfig.direction === 'ascending' ? -1 : 1;
                if (aVal > bVal) return this.state.sortConfig.direction === 'ascending' ? 1 : -1;
                return 0;
            });
        }
        return tacks;
    }

    handleRowClick(tack) {
        if (!tack || !tack.segment || tack.segment.length === 0) return;


        const segment = tack.segment;
        const windDir = this.props.target.wind_direction;

        // --- Graph Data Prep ---
        const graphData = segment.map(p => {
            const speed = (p._speed || p.speed || 0);
            const diff = this.normalizeDiff(p.direction, windDir);

            return {
                time: p.time.format("HH:mm:ss"),
                spd_s: diff < 0 ? speed : null,
                spd_p: diff >= 0 ? speed : null,
                rawSpeed: speed,
                lat: p.lat,
                lon: p.lon,
                direction: p.direction,
                speedObj: p
            };
        });

        // --- Markers & Dots ---
        const markers = [];
        const chartReferenceDots = [];

        // Max Speed Marker
        if (tack.maxSpeedPt) {
            const val = tack.maxSpeedPt._speed ?? tack.maxSpeedPt.speed ?? 0;
            markers.push({
                lat: tack.maxSpeedPt.lat,
                lon: tack.maxSpeedPt.lon,
                color: '#00BFFF', // DeepSkyBlue
                label: 'Max',
                value: val.toFixed(2) + " km/h"
            });
            chartReferenceDots.push({
                time: tack.maxSpeedPt.time.format("HH:mm:ss"),
                speed: val,
                r: 6,
                fill: '#00BFFF',
                stroke: 'white',
                strokeWidth: 2
            });
        }

        // Min Speed Marker
        if (tack.minSpeedPt) {
            const val = tack.minSpeedPt._speed ?? tack.minSpeedPt.speed ?? 0;
            markers.push({
                lat: tack.minSpeedPt.lat,
                lon: tack.minSpeedPt.lon,
                color: '#FF4500', // OrangeRed
                label: 'Min',
                value: val.toFixed(2) + " km/h"
            });
            chartReferenceDots.push({
                time: tack.minSpeedPt.time.format("HH:mm:ss"),
                speed: val,
                r: 6,
                fill: '#FF4500',
                stroke: 'white',
                strokeWidth: 2
            });
        }

        // --- Height Loss Line ---
        let heightLine = null;
        if (tack.pHigh && tack.pCross && tack.heightDiff !== "-") {
            heightLine = {
                pos: [[tack.pHigh.lat, tack.pHigh.lon], [tack.pCross.lat, tack.pCross.lon]],
                text: tack.heightDiff + "m"
            };
        }

        // --- Map Line ---
        const gpsLine = segment.map(p => {
            const diff = this.normalizeDiff(p.direction, windDir);
            return {
                pos: [p.lat, p.lon],
                isStb: diff < 0
            };
        });

        let latSum = 0, lonSum = 0;
        gpsLine.forEach(p => { latSum += p.pos[0]; lonSum += p.pos[1]; });
        const center = { lat: latSum / gpsLine.length, lng: lonSum / gpsLine.length };

        const firstP = graphData[0];
        const device = new Device(
            this.props.target.user.id,
            firstP.rawSpeed,
            0, firstP.direction || 0,
            { lat: firstP.lat, lng: firstP.lon },
            firstP.direction || 0,
            gpsLine, true, false, 0, 0, gpsLine,
            this.props.target.user.last_name,
            this.props.target.user.unit || "km/h",
            this.props.target.user.wind_unit || "m/s",
            [], [], [], []
        );

        const deviceMap = {};
        deviceMap[this.props.target.user.id] = device;

        this.setState({
            selectedTackIndex: tack.originalIndex,
            graphData,
            deviceMap,
            center,
            currentSegmentLines: gpsLine,
            markers,
            chartReferenceDots,
            heightLine
        });
    }

    handleMouseMove(e) {
        if (!e || !e.activePayload || !e.activePayload.length) return;

        const data = e.activePayload[0].payload;
        if (!data) return;

        const device = new Device(
            this.props.target.user.id,
            data.rawSpeed,
            0,
            data.direction,
            { lat: data.lat, lng: data.lon },
            data.direction,
            this.state.currentSegmentLines,
            true,
            false,
            0, 0,
            this.state.currentSegmentLines,
            this.props.target.user.last_name,
            this.props.target.user.unit || "km/h",
            this.props.target.user.wind_unit || "m/s",
            [], [], [], []
        );

        const deviceMap = {};
        deviceMap[this.props.target.user.id] = device;

        this.setState({ deviceMap });
    }

    handleChartClick(e) {
        if (!e || !e.activePayload || !e.activePayload.length) return;
        const clickedPt = e.activePayload[0].payload.speedObj;
        if (!clickedPt) return;

        if (this.state.selectedTackIndex !== null) {
            this.updateTackStats(this.state.selectedTackIndex, clickedPt);
        }
    }

    updateTackStats(originalIndex, newMaxSpeedPt) {
        const tacks = [...this.state.tacks];
        const tackIndex = tacks.findIndex(t => t.originalIndex === originalIndex);
        if (tackIndex === -1) return;

        const tack = tacks[tackIndex];
        const windDir = this.props.target.wind_direction;
        const minSpeedPt = tack.minSpeedPt;
        const segment = tack.segment;

        // 1. Update Entry Speed
        const entrySpeed = (newMaxSpeedPt._speed || newMaxSpeedPt.speed || 0);

        // 2. Update Entry Angle
        const entryAngle = (90 - Math.abs(this.normalizeDiff(newMaxSpeedPt.direction, windDir))).toFixed(0);

        // 3. Update Elapsed Time
        let elapsedTime = 0;
        if (minSpeedPt) {
            elapsedTime = minSpeedPt.time.diff(newMaxSpeedPt.time) / 1000;
        }

        // 4. Update Height Loss (Tack)
        // Use newMaxSpeedPt as pHigh (Start Point)
        const pHigh = newMaxSpeedPt;
        const baseLat = segment[0].lat;
        const baseLon = segment[0].lon;

        let pHighIndex = -1;
        for (let i = 0; i < segment.length; i++) {
            if (segment[i] === pHigh) {
                pHighIndex = i;
                break;
            }
        }

        // Upwind Ray Math
        const rad = Math.PI / 180;
        const upwindRad = windDir * rad;
        const vx = Math.sin(upwindRad);
        const vy = Math.cos(upwindRad);

        const R = 6371000;
        const ky = rad * R;
        const kx = rad * R * Math.cos(baseLat * rad);

        const toXY = (p) => ({
            x: (p.lon - baseLon) * kx,
            y: (p.lat - baseLat) * ky
        });

        const A = toXY(pHigh);

        let pCross = null;
        let heightDiffVal = 0;
        let bestT = Infinity;

        if (pHighIndex !== -1) {
            for (let k = pHighIndex + 1; k < segment.length - 1; k++) {
                const C = toXY(segment[k]);
                const D = toXY(segment[k + 1]);

                const dx_seg = D.x - C.x;
                const dy_seg = D.y - C.y;

                const det = vx * (-dy_seg) - vy * (-dx_seg);
                if (Math.abs(det) < 1e-9) continue;

                const diffX = C.x - A.x;
                const diffY = C.y - A.y;

                const t = (diffX * (-dy_seg) - diffY * (-dx_seg)) / det;
                const s = (vx * diffY - vy * diffX) / det;

                if (t > 1.0 && s >= 0 && s <= 1) {
                    if (t < bestT) {
                        bestT = t;
                        const ix = A.x + t * vx;
                        const iy = A.y + t * vy;
                        pCross = {
                            lat: (iy / ky) + baseLat,
                            lon: (ix / kx) + baseLon
                        };
                    }
                }
            }
        }

        let heightDiffStr = "-";
        if (pCross) {
            heightDiffVal = bestT;
            heightDiffStr = heightDiffVal.toFixed(0);
        }

        const updatedTack = {
            ...tack,
            entrySpeed: entrySpeed.toFixed(2),
            entryAngle,
            elapsedTime: elapsedTime.toFixed(1),
            heightDiff: heightDiffStr,
            maxSpeedPt: newMaxSpeedPt,
            pHigh: pHigh,
            pCross: pCross
        };

        tacks[tackIndex] = updatedTack;
        this.setState({ tacks }, () => {
            this.handleRowClick(updatedTack);
        });
    }

    render() {
        // Calculate Stats
        const processedTacks = this.getProcessedTacks();
        const totalTacks = processedTacks.length;
        const successCount = processedTacks.filter(j => j.result === "Success").length;
        const failCount = totalTacks - successCount;
        const successRate = totalTacks > 0 ? ((successCount / totalTacks) * 100).toFixed(1) : "0.0";

        return (
            <Container fluid style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
                {/* Top Half: Settings & Tack List */}
                <div style={{ flex: 1, overflow: 'auto', borderBottom: '1px solid #444', minHeight: 0 }}>
                    <Row className="mb-3" style={{ marginTop: '20px' }}>
                        <Col md={3}>
                            <label>Min Speed Threshold</label>
                            <input
                                type="number"
                                className="form-control"
                                value={this.state.minSpeedThreshold}
                                onChange={(e) =>
                                    this.setState({
                                        minSpeedThreshold: Number(e.target.value)
                                    }, this.handleAnalyze)
                                }
                            />
                        </Col>
                        <Col md={3}>
                            <label>Entry Speed Filter</label>
                            <input
                                type="number"
                                className="form-control"
                                value={this.state.entrySpeedThreshold}
                                onChange={(e) =>
                                    this.setState({
                                        entrySpeedThreshold: Number(e.target.value)
                                    }, this.handleAnalyze)
                                }
                            />
                        </Col>
                        <Col md={2}>
                            <label>Filter Turn</label>
                            <select
                                className="form-control"
                                value={this.state.filterTurn}
                                onChange={(e) => this.setState({ filterTurn: e.target.value })}
                            >
                                <option value="All">All</option>
                                <option value="Starboard">Starboard</option>
                                <option value="Port">Port</option>
                            </select>
                        </Col>
                        <Col md={2}>
                            <label>Filter Result</label>
                            <select
                                className="form-control"
                                value={this.state.filterResult}
                                onChange={(e) => this.setState({ filterResult: e.target.value }, this.handleAnalyze)}
                            >
                                <option value="All">All</option>
                                <option value="Success">Success</option>
                                <option value="Fail">Fail</option>
                            </select>
                        </Col>
                    </Row>

                    {/* Stats Row */}
                    <Row className="mb-2" style={{ color: 'black' }}>
                        <Col>
                            <span style={{ fontSize: '1.1em', marginRight: '20px' }}>
                                <strong>Total:</strong> {totalTacks}
                            </span>
                            <span style={{ fontSize: '1.1em', marginRight: '20px' }}>
                                <strong>Success Rate:</strong> {successRate}%
                            </span>
                            <span style={{ color: 'green', marginRight: '10px' }}>
                                (Success: {successCount})
                            </span>
                            <span style={{ color: 'red' }}>
                                (Fail: {failCount})
                            </span>
                        </Col>
                    </Row>

                    <Row>
                        <Col>
                            <Table striped bordered hover variant="dark">
                                <thead>
                                    <tr>
                                        <th>No.</th>
                                        <th onClick={() => this.requestSort('turnDirection')} style={{ cursor: 'pointer' }}>Turn {this.state.sortConfig.key === 'turnDirection' ? (this.state.sortConfig.direction === 'ascending' ? '▲' : '▼') : ''}</th>
                                        <th onClick={() => this.requestSort('entrySpeed')} style={{ cursor: 'pointer' }}>Entry Speed {this.state.sortConfig.key === 'entrySpeed' ? (this.state.sortConfig.direction === 'ascending' ? '▲' : '▼') : ''}</th>
                                        <th onClick={() => this.requestSort('entryAngle')} style={{ cursor: 'pointer' }}>Entry Angle {this.state.sortConfig.key === 'entryAngle' ? (this.state.sortConfig.direction === 'ascending' ? '▲' : '▼') : ''}</th>
                                        <th onClick={() => this.requestSort('minSpeed')} style={{ cursor: 'pointer' }}>Min Speed {this.state.sortConfig.key === 'minSpeed' ? (this.state.sortConfig.direction === 'ascending' ? '▲' : '▼') : ''}</th>
                                        <th onClick={() => this.requestSort('heightDiff')} style={{ cursor: 'pointer' }}>Height Loss (m) {this.state.sortConfig.key === 'heightDiff' ? (this.state.sortConfig.direction === 'ascending' ? '▲' : '▼') : ''}</th>
                                        <th onClick={() => this.requestSort('elapsedTime')} style={{ cursor: 'pointer' }}>Elapsed Time {this.state.sortConfig.key === 'elapsedTime' ? (this.state.sortConfig.direction === 'ascending' ? '▲' : '▼') : ''}</th>
                                        <th onClick={() => this.requestSort('result')} style={{ cursor: 'pointer' }}>Result {this.state.sortConfig.key === 'result' ? (this.state.sortConfig.direction === 'ascending' ? '▲' : '▼') : ''}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {processedTacks.map((tack, index) => {
                                        const isSelected = this.state.selectedTackIndex === tack.originalIndex;
                                        const isStarboard = tack.turnDirection.startsWith("Starboard");
                                        const isSuccess = tack.result === "Success";

                                        // Background based on Result (Success/Fail)
                                        const bgColor = isSuccess ? '#1b451b' : '#451b1b';

                                        // Box indicator color based on Turn Direction
                                        const turnIndicatorColor = isStarboard ? 'green' : 'red';

                                        return (
                                            <tr
                                                key={tack.originalIndex}
                                                onClick={() => this.handleRowClick(tack)}
                                                style={{
                                                    cursor: 'pointer',
                                                    backgroundColor: bgColor
                                                }}
                                            >
                                                <td>
                                                    {isSelected && <span style={{ color: '#FFD700', fontWeight: 'bold', marginRight: '5px' }}>✓</span>}
                                                    {index + 1}
                                                </td>
                                                <td>
                                                    <span style={{ color: turnIndicatorColor, marginRight: '8px' }}>■</span>
                                                    {tack.turnDirection}
                                                </td>
                                                <td>{tack.entrySpeed}</td>
                                                <td>{tack.entryAngle}</td>
                                                <td>{tack.minSpeed}</td>
                                                <td>{tack.heightDiff}</td>
                                                <td>{tack.elapsedTime}</td>
                                                <td
                                                    style={{
                                                        color: !isSuccess ? "salmon" : "lightgreen",
                                                        fontWeight: "bold"
                                                    }}
                                                >
                                                    {tack.result}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                    {processedTacks.length === 0 && (
                                        <tr>
                                            <td colSpan="8" className="text-center">
                                                No tacks analysis data
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </Col>
                    </Row>
                </div>

                {/* Bottom Half: Map & Graph */}
                <div style={{ flex: 1, display: 'flex', minHeight: 0, overflow: 'hidden' }}>
                    {/* Left: Map */}
                    <div style={{ flex: 1, borderRight: '1px solid #444', position: 'relative', overflow: 'hidden' }}>
                        <MapBrowser
                            zoom={this.state.zoom}
                            deviceMap={this.state.deviceMap}
                            center={this.state.center}
                            windSpeed={0}
                            windDirection={this.props.target ? this.props.target.wind_direction : 0}
                            windpos={{ lat: 0, lng: 0 }}
                            buoy_coordinate={[]}
                            authUser={this.props.authUser}
                            calcLine={[]}
                            markers={this.state.markers}
                            heightLine={this.state.heightLine}
                        />
                    </div>

                    {/* Right: Graph */}
                    <div style={{ flex: 1, padding: '10px', minWidth: 0, position: 'relative' }}>
                        <div style={{ position: 'absolute', top: 10, bottom: 10, left: 10, right: 10 }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={this.state.graphData} onMouseMove={this.handleMouseMove} onClick={this.handleChartClick}>
                                    <CartesianGrid stroke="#eee" strokeDasharray="5 5" />
                                    <XAxis dataKey="time" stroke="#ccc" />
                                    <YAxis reversed={this.state.yaxis} stroke="#ccc" />
                                    <Tooltip
                                        contentStyle={{ backgroundColor: '#222', border: '1px solid #444' }}
                                    />
                                    <Line
                                        type="monotone"
                                        dataKey="spd_s"
                                        stroke="#006400"
                                        isAnimationActive={false}
                                        dot={false}
                                        connectNulls={true}
                                        strokeWidth={2}
                                    />
                                    <Line
                                        type="monotone"
                                        dataKey="spd_p"
                                        stroke="#dc143c"
                                        isAnimationActive={false}
                                        dot={false}
                                        connectNulls={true}
                                        strokeWidth={2}
                                    />
                                    {this.state.chartReferenceDots.map((dot, idx) => (
                                        <ReferenceDot
                                            key={idx}
                                            x={dot.time}
                                            y={dot.speed}
                                            r={dot.r}
                                            fill={dot.fill}
                                            stroke={dot.stroke}
                                            strokeWidth={dot.strokeWidth}
                                        />
                                    ))}
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                        {this.state.graphData.length === 0 && (
                            <div style={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                color: '#aaa'
                            }}>
                                Select a tack to view details
                            </div>
                        )}
                    </div>
                </div>
            </Container >
        );
    }
}

const mapStateToProps = state => {
    return {
        target: state.PlayerSelector.target,
        gpsLog: state.TeamList.gpsLog,
        authUser: state.auth.user,
    };
};

export default connect(mapStateToProps)(Tack);
